/* eslint-disable @typescript-eslint/no-explicit-any */
import summaryCommonStore from "@/store/summaryCommon.store";
import { useNavigate } from "react-router-dom";
import { useMemo, useRef, useState, useEffect } from "react";
import ArrowLeftIcon from "@/assets/summary/arrow_left.svg";
import OriginPlateIcon from "@/assets/summary/ic_origin_plate.svg?react";
import DownloadIcon from "@/assets/common/ic_download.svg?react";
import OnlyViewIcon from "@/assets/common/ic_only_view.svg?react";
import Popover from "@/component/Popover";
import summarySrcStore from "@/store/summarySrc.store";
import IC_QA_ACTIVE from "@/assets/aiqa/ic_aiqa_active.svg?react";
import IC_QA_NORMAL from "@/assets/aiqa/ic_aiqa_normal.svg?react";
import IC_SHARE from "@/assets/common/ic_share.svg?react";
import AIQAStore from "@/store/AIQA.store";
import ExportPopover from "@/sections/WebSummary/Export";
import useLoginStore from "@/store/login.store";
import MoreIcon from "@/assets/summary/ic_more.svg";
import { IOptions } from "@/component/Select";
import { copyRecord } from "@/services/summary";
import { message } from "antd";
import { RuningInLink } from "@/constants";
import { deleteRecord, deleteShareRecord } from "@/services/api_list";
import usePermission from "@/sections/Summary/usePermission";
import Dialog from "@/component/Dialog";
import useCheckEditBeforeClick from "@/sections/Summary/useCheckEditBeforeClick";
import Tooltip from "@/component/Tooltip";
import TitleGeneratingIcon from "@/assets/summary/ic_title_generating.svg?react";
import { recordTitleMaxLen } from "@/constants/record";
import fridayReporter from "@/utils/friday";
import { Origin } from "@/constants/share";
import { formatSeconds2HMS } from "@/utils/formatter";
import summaryStore from "@/store/summary.store";
import summarySpeakerStore from "@/store/summarySpeaker.store";
import SpeakerAvatar from "@/pages/Voice/SpearkerAvatar";
import './index.less';
import { openShareModalV2 } from "@/sections/WebSummary/ShareModalV2";
import dayjs from 'dayjs';
import useGetPermission from "@/sections/Share/useGetPermission";
import { IS_PRIVATE } from "@/config";
import useExport from "@/sections/Summary/useExport";
import { openInternalWindow } from "@/utils/url";

export interface IHeaderProps {
  isOnlyBack?: boolean;
  titleEditable?: boolean;
  mode?: "summary" | "share";
}

const Header: React.FC<IHeaderProps> = ({ isOnlyBack = false, titleEditable = false, mode = "summary" }) => {
  const navigate = useNavigate();
  const generatedTitle = summarySrcStore((state) => state.generatedTitle);
  const titleGenerating = summarySrcStore((state) => state.titleGenerating);
  const detailInfo = summarySrcStore((state) => state.detailInfo);
  const submitModifyTitle = summarySrcStore((state) => state.submitModifyTitle);
  const initStore = summaryCommonStore((state) => state.initStore);
  const shareInfo = summaryCommonStore((state) => state.shareInfo);
  const summary = summaryStore((state) => state.summary);
  const { showExportBtn, speechDraftDone } = useExport();
  const speakerSummary = summarySpeakerStore((state) => state.speakerSummary);

  const [showExport, setShowExport] = useState(false);
  const [showSpeechDraftTip, setShowSpeechDraftTip] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [titleEditing, setTitleEditing] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const streamId = queryParams.get("streamId");

  const { isOwner, allowBackUp } = usePermission();
  const { mySharePermission } = useGetPermission(detailInfo?.streamName as string);

  const [showMoreList, setShowMoreList] = useState(false);

  const hasLogin = useLoginStore((state) => state.hasLogin);

  const toggleAIQAMode = AIQAStore((state) => state.toggleAIQAMode);
  const isAIQAMode = AIQAStore((state) => state.isAIQAMode);
  const [showDialog, setShowDialog] = useState(false);

  // 当发言稿选项出现时，显示提示气泡
  useEffect(() => {
    // TODOZE: 从simulInter页面跳转过来时，发言稿选项出现时，显示提示气泡
    if (speechDraftDone) {
      setShowSpeechDraftTip(true);
      // 3秒后自动隐藏
      const timer = setTimeout(() => {
        setShowSpeechDraftTip(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [speechDraftDone]);

  const showOnlyView = useMemo(() => {
    if(!mySharePermission ||!detailInfo) return false;
    return !isOwner && !detailInfo.allowBackUp;
  }, [mySharePermission, isOwner, detailInfo]);

  const moreList: Array<IOptions> =
    isOwner || allowBackUp
      ? [
        { id: "copy", label: "创建副本" },
        { id: "delete", label: "删除" },
      ]
      : [{ id: "delete", label: "删除" }];

  const handleClikAIQA = () => {
    fridayReporter.pushData({
      eventCode: 'SummaryPage_AI'
    })
    if (!detailInfo?.completeDocFinish) {
      message.warning("请等待原文生成完成");
      return;
    }
    if (!hasLogin) {
      const params = {
        redirectUrl: location.pathname + location.search,
      };
      const search = new URLSearchParams(params).toString();
      navigate(`/login?${search}`);
    } else {
      toggleAIQAMode();
    }
  };

  // 使用ref捕获最新值
  const latestValues = useRef({
    generatedTitle,
    detailInfo,
    summary,
    speakerSummary
  });

  latestValues.current = {
    generatedTitle,
    detailInfo,
    summary,
    speakerSummary
  };

  const reportShareData = () => {
    const current = latestValues.current;
    
    if (
      !current.generatedTitle ||
      !current.detailInfo ||
      !current.summary ||
      !current.speakerSummary
    ) {
      setTimeout(reportShareData, 1000);
      return;
    }

    fridayReporter.pushData({
      eventCode: 'SummaryPage_Share',
      payload: {
        meeting_title: current.generatedTitle,
        duration: current.detailInfo.duration 
          ? formatSeconds2HMS(current.detailInfo.duration)
          : '',
        content_sum: current.summary.srcAiGcContent?.content.length,
        todo_sum: current.summary.srcAiGcContent?.todo.length,
        spokesman_sum: current.speakerSummary.srcSpeakers?.length
      }
    });
  };

  const handleShare = async () => {
      openShareModalV2({
        streamName: detailInfo?.streamName || `live/${streamId}`,
        title: generatedTitle || "",
      });
    reportShareData();
  };

  const back = useCheckEditBeforeClick(() => {
    fridayReporter.pushData({
      eventCode: 'SummaryPage_ReturnHomepage'
    })
    navigate("/home");
    initStore();
    toggleAIQAMode(false);
  });

  const handleEditTitle = () => {
    if (!titleEditable || !isOwner) return;
    generatedTitle && setNewTitle(generatedTitle);
    setTitleEditing(true);
  };

  const onTitleChange = (e: string) => {
    setNewTitle(e);
  };

  const submitTitle = async () => {
    try {
      setTitleEditing(false);
      await submitModifyTitle(newTitle);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCopy = async () => {
    try {
      fridayReporter.pushData({
        eventCode: 'SummaryPage_CreatCopy'
      })
      const res = await copyRecord(detailInfo?.streamName);
      console.log("创建副本成功", res);
      message.success("创建副本成功");
      const newStreamId = (res.data as string)?.split("/")[1];
      if (!RuningInLink) {
        openInternalWindow(`/summary?streamId=${newStreamId}`);
      } else {
        navigate(`/summary?streamId=${newStreamId}`, { replace: true });
        toggleAIQAMode(false);
        location.reload();
      }
    } catch (error: any) {
      console.log('handleCopy error', error);
      // 录制信息已删除
      if (error.errCode === 40002103) {
        summaryCommonStore.getState().setErrorStatus(40002103);
        return;
      }
      if (error.errCode === 40002122) {
        message.error(`权限发生变更,${error.errMsg}`);
        summarySrcStore.getState().updateDetailInfo();
        return;
      }
      if (error.errCode === 40002104) {
        message.error(`权限发生变更,${error.errMsg}`);
        window.navigate(`/SummaryPermissionError?streamId=${streamId}`, {
          replace: true,
        });
        return;
      }
      message.error("创建副本失败");
    }
  };

  const handleDelete = async () => {
    fridayReporter.pushData({
      eventCode: 'SummaryPage_Delete'
    })
    setShowDialog(false);
    const streamName = detailInfo?.streamName || `live/${streamId}`;
    try {
      let res: boolean;
      if (isOwner) {
        res = await deleteRecord(streamName || "");
      } else {
        res = await deleteShareRecord(streamName || "");
      }
      if (streamName && res) {
        message.success("删除成功");
        navigate("/home");
      } else {
        message.error("删除失败");
      }
    } catch (error) {
      console.error("删除失败", streamName, error);
      message.error("删除失败");
    }
  };

  const handleMoreSelect = useCheckEditBeforeClick((value: IOptions) => {
    setShowMoreList(false);
    if (value.id === "copy") {
      handleCopy();
    } else if (value.id === "delete") {
      setShowDialog(true);
    }
  });

  const cancleDlelete = () => {
    setShowDialog(false);
  };

  const formatCreateTime = useMemo(() => {  
    if(!detailInfo?.createdDate) return '';  
    const createTime = detailInfo?.createdDate;
    if(dayjs(createTime).year === dayjs().year){
      return dayjs(createTime).format('MM-DD');
    }
    return dayjs(createTime).format('YY-MM-DD');
  }, [detailInfo?.createdDate])

  return (
    <div className={`h-16 px-6 py-2.25 flex items-center justify-between ${!RuningInLink && 'mt-2'} header`}>
      <Dialog isOpen={showDialog} title="确定删除" content="删除后，将无法恢复" onCancel={cancleDlelete} onConfirm={handleDelete}></Dialog>
      <div className="flex items-center overflow-hidden">
        {(!shareInfo || shareInfo.origin === Origin.MAX_LINK) && <img className="w-6 h-6 mr-3" src={ArrowLeftIcon} onClick={back} />}
        {!isOnlyBack && (
          <div className="overflow-hidden mr-4">
            {titleGenerating && !generatedTitle ? (
              <div className="h-6 flex items-center">
                <TitleGeneratingIcon className="size-4 mr-1" />
                <span className="color-#CED2D9 text-4">标题生成中...</span>
              </div>
            ) : (
              <div className="h-6">
                {titleEditing ? (
                  <input
                    autoFocus
                    maxLength={recordTitleMaxLen}
                    onChange={(e) => {
                      onTitleChange(e.target.value);
                    }}
                    value={newTitle}
                    className="bg-transparent b-1 b-solid b-mx-primary text-4 focus:b-mx-primary focus-outline-none px-2 py-1.5 rounded-1 lh-4 w-68 h-6"
                    onBlur={submitTitle}
                    onFocus={(e) => {
                      e.currentTarget.select();
                    }}
                    onKeyDown={(e) => {
                      e.key === "Enter" && submitTitle();
                    }}
                  />
                ) : (
                  <Tooltip
                    tooltipHelper={
                      <div className={`text-4 h-6`}>{generatedTitle}</div>
                    }
                    content={generatedTitle}
                    onlyShowWhenOverflow
                  >
                    <div
                      className={`text-4 h-6 select-text mx-text-ellipsis ${titleEditable && isOwner ? "hover:cursor-pointer" : ""
                        }`}
                      onClick={handleEditTitle}
                    >
                      {generatedTitle}
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
            {!shareInfo && (
              <div className="flex items-center text-3 text-#999 h-6 lh-6">
                {detailInfo?.origin && (
                  <div className="flex items-center mr-1">
                    {detailInfo?.origin === "MAXHUB会议平板" ? (
                      <OriginPlateIcon className="size-4" />
                    ) : (
                      <SpeakerAvatar
                        className={`size-4 text-1.5`}
                        avatarIndex={0}
                        speakerName={detailInfo.origin}
                      />
                    )}
                    <div className="text-3 text-#999999 pl-1">
                      {detailInfo.origin}
                    </div>
                  </div>
                )}  
                <div className="ml-1 text-3 text-#999999">{formatCreateTime ? `创建于 ${formatCreateTime}` : ''}</div>
                {showOnlyView && <div className="w-.25 h-2.5 bg-#CED2D9 mx-2"></div>}
                {showOnlyView &&<div className="flex items-center text-3 text-#999999">
                  <OnlyViewIcon className="mr-1" />
                  {`仅查看`}
                  </div>}
              </div>
            )}
          </div>
        )}
      </div>
      {!isOnlyBack && (
        <div className="flex items-center">
          <div id="summary-search" className="flex items-center mr-4"></div>

          {showExportBtn && (isOwner || detailInfo?.allowExport) && (
            <Tooltip
              content="发言稿已收纳至此处"
              placement="bottom"
              hover={false}
              visible={showSpeechDraftTip}
              // onVisibleChange={setShowSpeechDraftTip}
            >
              <Popover
                visible={showExport}
                setVisible={(v) => {
                  setShowExport(v);
                  if (v) {
                    fridayReporter.pushData({
                      eventCode: 'SummaryPage_Export'
                    })
                  }
                }}
                content={<ExportPopover detailInfo={detailInfo} generatedTitle={generatedTitle} />}
                placement="bottomRight"
              >
                <div className="size-8 flex items-center justify-center text-3.5 text-mx-secondary rounded-2.5 cursor-pointer">
                  <DownloadIcon className="size-4" />
                </div>
              </Popover>
            </Tooltip>
          )}

          {(mode === "summary" || (shareInfo?.origin === Origin.MAX_LINK && hasLogin)) && showExportBtn && (
            <Popover
              placement="bottomRight"
              visible={showMoreList}
              setVisible={setShowMoreList}
              content={
                <div>
                  {moreList.map((item) => {
                    return (
                      <div
                        key={item.id}
                        className={`${!detailInfo && "op-60 cursor-not-allowed"
                          } w-auto min-w-32.5 whitespace-nowrap h-8 px-2 py-1.25 text-size-3.5 text-mx-secondary hover:bg-#191D260D rd-1.5 flex flex-items-center flex-justify-between cursor-pointer`}
                        onClick={() => {
                          if (!detailInfo) return;
                          handleMoreSelect(item);
                        }}
                      >
                        <span>{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              }
            >
              <div className="m-l-4 size-8 cursor-pointer flex items-center justify-center relative">
                <img className="size-4" src={MoreIcon} />
              </div>
            </Popover>
          )}

          {
            <>
              <div className="m-l-4 w-18 h-8 bg-#383B50 flex items-center justify-center text-3.5 text-mx-white rounded-2.5 cursor-pointer" onClick={handleShare}>
                <IC_SHARE className="size-4.5" />
                <span className='ml-1 text-3.5'>分享</span>
              </div>
            </>
          }

          {!IS_PRIVATE && (
            <div className={`m-l-4 w-18 h-8 cursor-pointer rounded-2.5 flex items-center justify-center ${isAIQAMode ? "aiqa-active" : "aiqa-normal"}`}
            onClick={() => {
              handleClikAIQA();
            }}>
            {isAIQAMode ? <IC_QA_ACTIVE className="size-4.5" /> : <IC_QA_NORMAL className="size-4.5" />}
            <span className='ml-1 text-3.5'>问答</span>
          </div>
          )}

          {/* {!RuningInLink && hasLogin && (
            <div className="flex items-center">
              <div className="w-.25 h-6 bg-#DBDEE4 mx-4"></div>
              <User />
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

export default Header;
